import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { Badge } from "../ui/badge";
import {
  X,
  ChevronRight,
  ChevronLeft,
  Sparkles,
  Target,
  BookOpen,
  Award,
  Share2,
  QrCode,
  Gift,
  CheckCircle,
  Rocket
} from "lucide-react";

const TUTORIAL_STEPS = [
  {
    id: "welcome",
    title: "Welcome to Your Ambassador Dashboard! 🎉",
    description: "Let's take a quick tour to help you start earning rewards. This will only take 2 minutes!",
    icon: Rocket,
    position: "center",
    highlight: null
  },
  {
    id: "overview",
    title: "Your Overview",
    description: "This tab shows your key stats: total referrals, sales you've generated, and commissions earned. Watch these numbers grow as you share!",
    icon: Target,
    position: "top",
    highlight: '[value="overview"]',
    tab: "overview"
  },
  {
    id: "referral-link",
    title: "Your Unique Referral Link",
    description: "This is YOUR personal link. When someone clicks it and makes a purchase, you earn commission! Copy it and share it everywhere.",
    icon: Share2,
    position: "bottom",
    highlight: '[data-testid="referral-link-section"]',
    tab: "overview"
  },
  {
    id: "qr-code",
    title: "QR Code for Easy Sharing",
    description: "Click the QR icon to get a scannable code. Perfect for in-person events, business cards, or social media stories!",
    icon: QrCode,
    position: "left",
    highlight: '[title="Get QR Code"]',
    tab: "overview"
  },
  {
    id: "missions",
    title: "Complete Missions for Bonus Points",
    description: "Missions are tasks that help you learn and earn extra points. Start with onboarding missions, then progress to bigger challenges!",
    icon: Target,
    position: "top",
    highlight: '[value="missions"]',
    tab: "missions"
  },
  {
    id: "weekly-challenge",
    title: "Weekly Challenges",
    description: "Each week brings a new challenge with bonus points! These reset every Sunday, so don't miss out on extra rewards.",
    icon: Sparkles,
    position: "bottom",
    highlight: null,
    tab: "missions"
  },
  {
    id: "resources",
    title: "Your Marketing Toolkit",
    description: "Find ready-to-use captions, hashtags, story templates, and platform tips. Everything you need to create engaging posts!",
    icon: BookOpen,
    position: "top",
    highlight: '[value="resources"]',
    tab: "resources"
  },
  {
    id: "points",
    title: "Track & Redeem Points",
    description: "See your points balance, tier status, and progress. Redeem points for store credit to get free artwork!",
    icon: Award,
    position: "top",
    highlight: '[value="points"]',
    tab: "points"
  },
  {
    id: "tiers",
    title: "Climb the Ranks",
    description: "Earn more points to unlock higher tiers: Bronze → Silver → Gold → Platinum. Each tier gives you bonus earning multipliers!",
    icon: Gift,
    position: "bottom",
    highlight: null,
    tab: "points"
  },
  {
    id: "complete",
    title: "You're Ready! 🚀",
    description: "Start by sharing your referral link on social media. Complete your first mission to earn 50 bonus points. Good luck!",
    icon: CheckCircle,
    position: "center",
    highlight: null
  }
];

const DashboardTutorial = ({ onComplete, onDismiss }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isVisible, setIsVisible] = useState(true);

  const step = TUTORIAL_STEPS[currentStep];
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === TUTORIAL_STEPS.length - 1;
  const progress = ((currentStep + 1) / TUTORIAL_STEPS.length) * 100;

  // Switch tabs when tutorial step changes
  useEffect(() => {
    if (step.tab) {
      const tabTrigger = document.querySelector(`[value="${step.tab}"]`);
      if (tabTrigger) {
        tabTrigger.click();
      }
    }
  }, [step.tab]);

  // Highlight element when step changes
  useEffect(() => {
    // Remove previous highlights
    document.querySelectorAll('.tutorial-highlight').forEach(el => {
      el.classList.remove('tutorial-highlight');
    });

    if (step.highlight) {
      const element = document.querySelector(step.highlight);
      if (element) {
        element.classList.add('tutorial-highlight');
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }

    return () => {
      document.querySelectorAll('.tutorial-highlight').forEach(el => {
        el.classList.remove('tutorial-highlight');
      });
    };
  }, [step.highlight]);

  const handleNext = () => {
    if (isLastStep) {
      handleComplete();
    } else {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrev = () => {
    if (!isFirstStep) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleComplete = () => {
    setIsVisible(false);
    localStorage.setItem('ambassador_tutorial_completed', 'true');
    onComplete?.();
  };

  const handleDismiss = () => {
    setIsVisible(false);
    localStorage.setItem('ambassador_tutorial_dismissed', 'true');
    onDismiss?.();
  };

  if (!isVisible) return null;

  const IconComponent = step.icon;

  const getPositionClasses = () => {
    switch (step.position) {
      case 'top':
        return 'top-24 left-1/2 -translate-x-1/2';
      case 'bottom':
        return 'bottom-24 left-1/2 -translate-x-1/2';
      case 'left':
        return 'top-1/2 left-8 -translate-y-1/2';
      case 'right':
        return 'top-1/2 right-8 -translate-y-1/2';
      default:
        return 'top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2';
    }
  };

  return (
    <>
      {/* Backdrop */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 z-50"
        onClick={handleDismiss}
      />

      {/* Tutorial Card */}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentStep}
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: -20 }}
          transition={{ duration: 0.3 }}
          className={`fixed z-50 w-full max-w-md px-4 ${getPositionClasses()}`}
        >
          <Card className="shadow-2xl border-2 border-amber-200 bg-white">
            <CardContent className="p-6">
              {/* Close button */}
              <button
                onClick={handleDismiss}
                className="absolute top-3 right-3 text-muted-foreground hover:text-foreground transition-colors"
              >
                <X className="w-5 h-5" />
              </button>

              {/* Progress bar */}
              <div className="mb-4">
                <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                  <span>Step {currentStep + 1} of {TUTORIAL_STEPS.length}</span>
                  <span>{Math.round(progress)}%</span>
                </div>
                <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                  <motion.div
                    className="h-full bg-gradient-to-r from-amber-400 to-amber-600"
                    initial={{ width: 0 }}
                    animate={{ width: `${progress}%` }}
                    transition={{ duration: 0.3 }}
                  />
                </div>
              </div>

              {/* Icon and title */}
              <div className="flex items-start gap-4 mb-4">
                <div className="p-3 rounded-xl bg-gradient-to-br from-amber-400 to-amber-600 text-white shadow-lg">
                  <IconComponent className="w-6 h-6" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold mb-1">{step.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {step.description}
                  </p>
                </div>
              </div>

              {/* Step indicators */}
              <div className="flex justify-center gap-1.5 mb-4">
                {TUTORIAL_STEPS.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentStep(idx)}
                    className={`w-2 h-2 rounded-full transition-all ${
                      idx === currentStep 
                        ? 'bg-amber-500 w-6' 
                        : idx < currentStep 
                          ? 'bg-amber-300' 
                          : 'bg-muted'
                    }`}
                  />
                ))}
              </div>

              {/* Navigation buttons */}
              <div className="flex gap-2">
                {!isFirstStep && (
                  <Button
                    variant="outline"
                    onClick={handlePrev}
                    className="flex-1"
                  >
                    <ChevronLeft className="w-4 h-4 mr-1" />
                    Back
                  </Button>
                )}
                <Button
                  onClick={handleNext}
                  className={`flex-1 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 ${isFirstStep ? 'w-full' : ''}`}
                >
                  {isLastStep ? (
                    <>
                      <CheckCircle className="w-4 h-4 mr-1" />
                      Get Started!
                    </>
                  ) : (
                    <>
                      Next
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </>
                  )}
                </Button>
              </div>

              {/* Skip link */}
              {!isLastStep && (
                <button
                  onClick={handleDismiss}
                  className="w-full text-center text-xs text-muted-foreground hover:text-foreground mt-3 transition-colors"
                >
                  Skip tutorial
                </button>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </AnimatePresence>

      {/* CSS for highlight effect */}
      <style>{`
        .tutorial-highlight {
          position: relative;
          z-index: 51;
          box-shadow: 0 0 0 4px rgba(251, 191, 36, 0.5), 0 0 20px rgba(251, 191, 36, 0.3);
          border-radius: 8px;
          animation: pulse-highlight 2s ease-in-out infinite;
        }

        @keyframes pulse-highlight {
          0%, 100% {
            box-shadow: 0 0 0 4px rgba(251, 191, 36, 0.5), 0 0 20px rgba(251, 191, 36, 0.3);
          }
          50% {
            box-shadow: 0 0 0 8px rgba(251, 191, 36, 0.3), 0 0 30px rgba(251, 191, 36, 0.2);
          }
        }
      `}</style>
    </>
  );
};

// Hook to check if tutorial should be shown
export const useTutorialState = () => {
  const [showTutorial, setShowTutorial] = useState(false);
  const [hasChecked, setHasChecked] = useState(false);

  useEffect(() => {
    const completed = localStorage.getItem('ambassador_tutorial_completed');
    const dismissed = localStorage.getItem('ambassador_tutorial_dismissed');
    
    if (!completed && !dismissed) {
      // Show tutorial after a short delay for first-time users
      const timer = setTimeout(() => {
        setShowTutorial(true);
      }, 1500);
      return () => clearTimeout(timer);
    }
    setHasChecked(true);
  }, []);

  const startTutorial = () => setShowTutorial(true);
  const endTutorial = () => setShowTutorial(false);
  const resetTutorial = () => {
    localStorage.removeItem('ambassador_tutorial_completed');
    localStorage.removeItem('ambassador_tutorial_dismissed');
    setShowTutorial(true);
  };

  return { showTutorial, startTutorial, endTutorial, resetTutorial, hasChecked };
};

export default DashboardTutorial;
